import type React from 'react'
import type { JSX } from 'react'

import { useState } from 'react'
import { Corners, Height, ProgressBar } from 'atoms/ProgressBar'
import {
  TextAligning,
  TextBox,
  TextBoxColors,
  TextBoxTypes,
} from 'atoms/TextBox'
import { COLOR } from 'vars/index'
import { CARDS_ASPECT_RATIO, CardCorners } from '../Card.h'
import { TVSimpleCard } from '../TVCard/TVSimpleCard'
import { formatChannelLabel, toChannelNumber } from './lib'
import {
  ProgrammeCardChannelSC,
  ProgrammeCardDescriptionSC,
  ProgrammeCardFavoriteSC,
  ProgrammeCardFooterSC,
  ProgrammeCardLogoContainerSC,
  ProgrammeCardLogoSC,
  ProgrammeCardRootSC,
  ProgrammeCardSC,
  programmeCardTitleStyles,
  ProgrammeCardTimeSC,
} from './ProgrammeCard.styled'

export interface IProgrammeCardProps {
  // logic
  /** My-list button, shown top-right on card hover when `viewedButtonFavorite` is set (UI-5.1/5.2). */
  children?: React.ReactNode

  /** Channel logo; omitted entirely when absent or pinlocked (UI-5.3) */
  logoUrl?: string
  channelNumber?: number | string
  channelName?: string
  /** EPG programme title; omitted when the channel has no EPG data (UI-5.5) */
  programmeTitle?: string
  /** Elapsed percentage, Watch Now only (UI-5.6) */
  progress?: number | null
  /** "5m left" (Watch Now, UI-5.7) or "Apr 25, 7:30 PM - 9:00 PM" (Catchups, UI-5.8) */
  timeLabel?: string
  /** Pinlocked channel: reuse the TV channel lock overlay; hide programme metadata */
  locked?: boolean
  /** Geoblocked channel: hide programme metadata, keep the channel it belongs to (UI-5.5-5.8) */
  aclBlockedBy?: boolean
  /** My-list control on the pinlocked / geoblocked TV-card hover overlay */
  viewedButtonFavorite?: boolean
  /** Paid channel: crown badge (same as TV channel cards) */
  paid?: boolean
  /** Already-translated short "new content" badge text (Channels, top-right). */
  newLabelShort?: string
  /** Blurred cover set used by the locked/geo TV-channel layout */
  covers?: Record<string, any>
  onClick?: () => void

  // styles
  cardAspectRatio?: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  gridCard?: boolean
  backgroundColorDefault?: COLOR
  channelColor?: TextBoxColors
  channelFont?: TextBoxTypes
  titleColor?: TextBoxColors
  titleFont?: TextBoxTypes
  timeColor?: TextBoxColors
  timeFont?: TextBoxTypes
  corners?: CardCorners
}

export const ProgrammeCard = ({
  children,
  logoUrl,
  channelNumber,
  channelName,
  programmeTitle,
  progress,
  timeLabel,
  locked = false,
  aclBlockedBy = false,
  viewedButtonFavorite = false,
  paid = false,
  newLabelShort,
  covers,
  onClick,
  cardAspectRatio = CARDS_ASPECT_RATIO.HORIZONTAL_DEFAULT,
  gridCard = true,
  // FP-4305: Figma `surface/dark5`, same fill as TVSimpleCard channel cards
  backgroundColorDefault = COLOR.SURFACE_DARK_5,
  channelColor = TextBoxColors.TEXT_ICON_SECONDARY,
  channelFont = TextBoxTypes.FontXs,
  titleColor = TextBoxColors.TEXT_ICON_PRIMARY,
  titleFont = TextBoxTypes.FontSmBold,
  timeColor,
  // Same 14px FontXs for Watch Now "Xm left" and the catch-up date-time label.
  timeFont = TextBoxTypes.FontXs,
  corners = CardCorners.CORNERS_0,
}: IProgrammeCardProps): JSX.Element => {
  // Skin list keys are positional, so a reused card must recover when logoUrl
  // changes. Store the URL that failed rather than a boolean so a new src is
  // shown without an effect-driven flash (FP-4293).
  const [failedLogoUrl, setFailedLogoUrl] = useState<string>()

  // CR-409 UI-5.1/5.2: pinlocked / geoblocked programmes reuse the TV channel
  // card states (lock overlay, globe overlay, cover, channel number).
  if (locked || aclBlockedBy) {
    return (
      <TVSimpleCard
        posterUrl={logoUrl ?? ''}
        locked={locked}
        aclBlockedBy={aclBlockedBy}
        paid={paid}
        covers={covers}
        channelNumber={toChannelNumber(channelNumber)}
        title={channelName ?? ''}
        viewedButtonFavorite={viewedButtonFavorite}
        aspectRatio={cardAspectRatio}
        gridCard={gridCard}
        corners={corners}
        onClick={onClick}
        backgroundColor={backgroundColorDefault}
        hidePriceOnHover={false}
        newLabelShort={newLabelShort}
      >
        {children ?? <></>}
      </TVSimpleCard>
    )
  }

  const channelLabel = formatChannelLabel({
    channelNumber,
    channelName,
  })
  const showLogo = Boolean(logoUrl) && failedLogoUrl !== logoUrl
  const showProgrammeTitle = Boolean(programmeTitle)
  // Watch Now is the only variant with elapsed progress, and the design gives it
  // its own indicator: the time left sits bottom right above a full-width track,
  // while a catch-up date-time label stays bottom left (UI-5.6-5.8). `ProgressBar`
  // paints nothing without elapsed time, so the footer follows the same rule and
  // keeps the label off the card edge. The time row still renders when the
  // remaining-time text is empty so channel logo and titles stay aligned.
  const showProgress = Boolean(progress)
  const showTimeLabel = Boolean(timeLabel)
  // Watch Now always passes `progress` (including 0); catch-up omits it.
  // Do not reuse `showProgress` — 0% elapsed is still a live time-left label.
  const resolvedTimeColor =
    timeColor ??
    (progress != null
      ? TextBoxColors.TEXT_ICON_PRIMARY
      : TextBoxColors.TEXT_ICON_SECONDARY)

  return (
    <ProgrammeCardRootSC
      backgroundColor={backgroundColorDefault}
      aspectRatio={cardAspectRatio}
      gridCard={gridCard}
      posterEnabled={false}
      paid={paid}
      hidePriceOnHover={false}
      onClick={onClick}
      corners={corners}
      newLabelShort={newLabelShort}
    >
      <>
        {viewedButtonFavorite && children && (
          <ProgrammeCardFavoriteSC>{children}</ProgrammeCardFavoriteSC>
        )}
        <ProgrammeCardSC>
          <ProgrammeCardDescriptionSC>
            <ProgrammeCardChannelSC>
              {showLogo && (
                <ProgrammeCardLogoContainerSC>
                  <ProgrammeCardLogoSC
                    src={logoUrl}
                    alt=''
                    onError={() => setFailedLogoUrl(logoUrl)}
                  />
                </ProgrammeCardLogoContainerSC>
              )}
              {channelLabel && (
                <TextBox
                  title={channelLabel}
                  font={channelFont}
                  color={channelColor}
                  maxLines={1}
                />
              )}
            </ProgrammeCardChannelSC>

            {showProgrammeTitle && (
              <TextBox
                title={programmeTitle}
                font={titleFont}
                color={titleColor}
                maxLines={2}
                className={programmeCardTitleStyles}
              />
            )}
          </ProgrammeCardDescriptionSC>

          {(showTimeLabel || showProgress) && (
            <ProgrammeCardFooterSC withProgress={showProgress}>
              <ProgrammeCardTimeSC
                alignEnd={showProgress}
                data-programme-card-time=''
              >
                {showTimeLabel && (
                  <TextBox
                    title={timeLabel}
                    font={timeFont}
                    color={resolvedTimeColor}
                    maxLines={1}
                    alignment={
                      showProgress ? TextAligning.Right : TextAligning.Left
                    }
                  />
                )}
              </ProgrammeCardTimeSC>
              {showProgress && (
                <ProgressBar
                  progress={progress}
                  height={Height.SIZE_10}
                  corners={Corners.CORNERS_0}
                  background={COLOR.SURFACE_OPACITY_LIGHT_10}
                  color={COLOR.PROGRESS_BAR_ACCENT_SECONDARY}
                />
              )}
            </ProgrammeCardFooterSC>
          )}
        </ProgrammeCardSC>
      </>
    </ProgrammeCardRootSC>
  )
}
